import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { SmsCampaignFormInput } from '../SmsCampaignValidation';
import {
  PrepareConnectorFlowStepResponse,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { ActionButton } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/ActionButton';
import ErrorBoundary from '@/components/ErrorBoundary';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCompanyValues } from '@/providers';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowMappingsForm, FlowMappingsFormHandle } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { StepPageActions } from '@/global/components/Layout/StepPageActions/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { FlowFormData, FlowMappingsConfig, FlowMappingsData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { SMS_CONNECTOR_FLOW_NAME } from '@/features/campaigns/v2/Sms/constants';
import { useSmsWorkspace } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import { Button } from '@/lib';
import RouteConstants from '@/features/router/RouteConstants';

type SenderSelectionStepProps = {
  onBack: () => void;
  onNext: (formData: FlowFormData, mappingsData: FlowMappingsData) => Promise<boolean>;
};

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

const hasMappingsUi = (config: FlowMappingsConfig) => !!config?.schema?.properties && Object.keys(config.schema.properties).length > 0;

const TEMPLATE_ID_KEYS = ['template', 'template_id', 'templateId'] as const;

const pickTemplateIdFromFlowParams = (params: FlowFormData): string => {
  for (const key of TEMPLATE_ID_KEYS) {
    const value = params[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
};

export const SenderSelectionStep: FC<SenderSelectionStepProps> = ({ onBack, onNext }) => {
  const { id: workspaceId } = useWorkspace();
  const { connectorId } = useSmsWorkspace();
  const history = useHistory();
  const { setValue, watch } = useFormContext<SmsCampaignFormInput>();
  const selectedAudienceId = watch('audienceId');

  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const { t: tCommon } = useTranslation('COMMON');

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [step1Completed, setStep1Completed] = useState(false);
  const mappingsFormRef = useRef<FlowMappingsFormHandle>(null);

  const { data: connectorFlows } = useGetConnectorFlowsQuery({
    workspaceId,
    queryParams: {
      showUserJourney: true,
    },
  });
  const {
    data: connectorInstances,
    isLoading: isLoadingInstances,
    refetch: refetchInstances,
  } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  const { currentLanguage } = useCompanyValues();

  const smsConnectorFlow = connectorFlows?.find((flow) => flow.name === SMS_CONNECTOR_FLOW_NAME && flow.connectorId === connectorId);
  const smsConnectorFlowId = smsConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === connectorId)?.id;

  const [prepareStep, { isLoading: isPreparing }] = usePrepareConnectorFlowStepMutation();

  useEffect(() => {
    setValue('connectorId', connectorId || '');
    setValue('connectorFlowId', smsConnectorFlowId || '');
  }, [smsConnectorFlowId, connectorId, setValue]);

  const fetchPrepareData = useCallback(async () => {
    if (!selectedAudienceId || !smsConnectorFlowId) return;
    if (!connectorInstanceId) {
      setPrepareError(String(t('whatsappWorkspace.noWhatsappConnector')));
      return;
    }

    setPrepareError(null);

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: smsConnectorFlowId,
        audienceId: selectedAudienceId,
        stepId: 'step1',
        connectorInstanceId,
        stepData: {},
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    } catch {
      setPrepareError(t('smsWorkspace.campaigns.failedToLoadTemplateOptions'));
      setPrepareResult(null);
    }
  }, [selectedAudienceId, smsConnectorFlowId, connectorInstanceId, prepareStep, workspaceId, currentLanguage, t]);

  useEffect(() => {
    setPrepareResult(null);
    setPrepareError(null);
    setFormData({});
    setMultiStepMappingsConfig(null);
    setMappingData({});
    setIsMappingsFormValid(false);
    setStep1Completed(false);
    setSubmitError(null);
  }, [selectedAudienceId, smsConnectorFlowId, connectorInstanceId]);

  useEffect(() => {
    if (!smsConnectorFlowId || !selectedAudienceId) return;
    void fetchPrepareData();
  }, [fetchPrepareData, smsConnectorFlowId, selectedAudienceId, connectorInstanceId]);

  const persistTemplateId = useCallback(
    (params: FlowFormData) => {
      const templateId = pickTemplateIdFromFlowParams(params);
      setValue('templateId', templateId, { shouldValidate: true, shouldTouch: true });
    },
    [setValue]
  );

  const handleFlowComplete = useCallback(
    async (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setSubmitError(null);
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);

      // Alias Insert / single-step campaign flows have only step1 and no mappings UI —
      // advance immediately instead of waiting for a confirm/mappings screen.
      if (!hasMappingsUi(mappingsConfig)) {
        persistTemplateId(allParams);
        await onNext(allParams, {});
        return;
      }

      setStep1Completed(true);
    },
    [onNext, persistTemplateId]
  );

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFinalSubmit = async () => {
    if (!isMappingsFormValid) {
      mappingsFormRef.current?.revealValidationErrors();
      return;
    }
    persistTemplateId(formData);
    await onNext(formData, mappingData);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: onBack,
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: handleFinalSubmit,
  };

  const pageTitle = t('smsWorkspace.campaigns.senderSelectionPageTitle');
  const showMappings = step1Completed && hasMappingsUi(multiStepMappingsConfig);

  if (!smsConnectorFlowId) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>Initializing...</p>
      </div>
    );
  }

  if (isPreparing) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>Loading...</p>
      </div>
    );
  }

  if (!connectorInstanceId && !isLoadingInstances) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-red-600'>{t('smsWorkspace.noSmsConnector')}</p>
        <ActionButton handleBack={onBack} handleNext={async () => void (await refetchInstances())} isNextDisabled={false} isLoading={false} />
      </div>
    );
  }

  if (prepareError) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-red-600'>{prepareError}</p>
        <ActionButton handleBack={onBack} handleNext={fetchPrepareData} isNextDisabled={false} isLoading={false} />
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>{t('smsWorkspace.campaigns.selectTemplatePageDescription')}</p>
      </div>
      {!step1Completed && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm
              audienceId={selectedAudienceId!}
              connectorFlowId={smsConnectorFlowId!}
              connectorInstanceId={connectorInstanceId!}
              paramsConfig={prepareResult}
              templatePreviewVariant='sms'
              onComplete={handleFlowComplete}
            />
          ) : (
            <div className='flex flex-col items-start gap-4 rounded-xl border border-dashed border-secondary-border bg-gray-50 px-5 py-8'>
              <div className='space-y-1'>
                <p className='text-base font-semibold text-slate-900'>{t('smsWorkspace.campaigns.noTemplatesCreateTitle')}</p>
                <p className='text-sm text-secondary-600'>{t('smsWorkspace.campaigns.noTemplatesCreateDescription')}</p>
              </div>
              <div className='flex flex-col gap-3 sm:flex-row'>
                <Button intent='secondary' onClick={onBack} text={tCommon('back')} />
                <Button
                  onClick={() => history.push(RouteConstants.createSmsTemplate)}
                  text={t('smsWorkspace.campaigns.createDisabledBannerNoTemplatesAction')}
                />
              </div>
            </div>
          )}
        </ErrorBoundary>
      )}
      {submitError && <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{submitError}</div>}
      {showMappings && multiStepMappingsConfig ? (
        <ErrorBoundary>
          <div>
            <h3 className='mb-4 text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              ref={mappingsFormRef}
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
